import React from "react";
import { motion } from "framer-motion";
import {
  Compass,
  TrendingUp,
  Shield,
  Briefcase,
  Receipt,
  Landmark,
  Check,
} from "lucide-react";
import { SITE } from "@/data/site";

const ICONS = {
  compass: Compass,
  "trending-up": TrendingUp,
  shield: Shield,
  briefcase: Briefcase,
  receipt: Receipt,
  landmark: Landmark,
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative bg-[#08080A] py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#F5A623] font-semibold">
              <span className="divider-gold" /> Services
            </span>
            <h2
              data-testid="services-headline"
              className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.08] tracking-tight silver-text"
            >
              Everything your family's wealth needs — under one roof.
            </h2>
          </div>
          <p className="text-[#B7BBC4] max-w-md text-base leading-relaxed">
            From your first home down-payment to multigenerational estate transfer, we cover the whole arc.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SITE.services.map((cat, i) => {
            const Icon = ICONS[cat.icon] || Compass;
            return (
              <motion.article
                key={cat.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: i * 0.05 }}
                className="glass-card rounded-md p-7 hover:-translate-y-1 transition-all duration-300"
                data-testid={`service-card-${cat.title.toLowerCase().replace(/\W+/g, "-")}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-md bg-white/[0.04] border border-[#F5A623]/30 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#F5A623]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-xl text-white">{cat.title}</h3>
                </div>
                <ul className="mt-6 space-y-2.5">
                  {cat.items.map((it) => (
                    <li
                      key={it}
                      className="flex items-start gap-2.5 text-sm text-[#B7BBC4]"
                    >
                      <Check className="w-4 h-4 text-[#F5A623] mt-0.5 shrink-0" strokeWidth={2} />
                      {it}
                    </li>
                  ))}
                </ul>
              </motion.article>
            );
          })}
        </div>

        {/* Reference-based services */}
        <div className="mt-20 glass-card rounded-md p-8 lg:p-10">
          <div className="grid lg:grid-cols-3 gap-8 items-start">
            <div>
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] text-[#F5A623] font-semibold">
                <span className="divider-gold" /> Reference-Based
              </span>
              <h3 className="mt-4 font-serif text-2xl lg:text-3xl silver-text">
                Trusted partners for everything else.
              </h3>
              <p className="mt-3 text-sm text-[#9CA0AA]">
                We connect you with vetted specialists for adjacent needs — so your financial life stays cohesive.
              </p>
            </div>
            <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
              {SITE.referenceServices.map((s) => (
                <div
                  key={s}
                  data-testid={`reference-${s.toLowerCase().replace(/\W+/g, "-")}`}
                  className="border border-white/8 bg-white/[0.02] rounded-md px-4 py-3 text-sm text-[#ECECEE] hover:border-[#F5A623] hover:text-[#F5A623] hover:bg-white/[0.04] transition-all duration-300"
                >
                  {s}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
